import { mapGetters, mapActions } from 'vuex';
import dayjs from 'dayjs';
import XLSX from 'xlsx';
import Modal from '@/components/Modal.vue';
import Daterangepicker from '@/components/Daterangepicker.vue';
import Overview from '@/components/Overview.vue';
import PriceDisplay from '@/components/PriceDisplay.vue';
import WalletHistoryDetails from '@/components/WalletHistoryDetails.vue';
import WalletHistoryMetas from '@/components/WalletHistoryMetas.vue';
import MutationValue from '@/components/MutationValue.vue';
import {
	duplicateVar,
	getDateRange,
	downloadFile,
	getAxiosErrorMessage,
	formatNumberPlain,
	sensorEmailAddress, generateTimerLabel, toTitleCase } from '@/lib/helper';
import walletApi from '@/api/wallet';
import walletStashApi from '@/api/wallet-stash';
import otpApi from '@/api/otp';
import user from '@/api/user';

const defaultFilterDate = getDateRange();

export default {
	name: 'Wallet',
	components: {
		Modal,
		Daterangepicker,
		Overview,
		PriceDisplay,
		WalletHistoryDetails,
		WalletHistoryMetas,
		MutationValue,
	},
	data() {
		return {
			keyword: '',
			limit: 25,
			page: 1,
			sort_by: 'id',
			sort: 'desc',
			histories: [],
			history: null,
			isFetching: false,
			isAll: false,
			filterDate: defaultFilterDate,
			isWithdrawalVisible: false,
			isWithdrawing: false,
			isExporting: false,
			withdrawValue: 1,
			withdrawNotes: '',
			currency: 'Rp',
			isFetchingBalance: false,
			isFetchingWithdrawals: false,
			balance: 0,
			withdrawn: 0,
			withdrawTreshold: 0,
			bankAccount: null,
			overviews: null,
			password: '',
			isFetchingStash: false,
			stashTotal: 0,
			// isOtpValid: false,
			isGeneratingOtp: false,
			isOtpVisible: false,
			otpCode: '',
			otpTimerValue: 300,
			otpTimerInterval: null,
			maxOtpTime: 300,
			isCheckingPassword: false,
			typeSelected: 'all',
			statusSelected: 'all',
			orderId: null,
		};
	},
	methods: {
		...mapActions({
		}),
		resetHistories() {
			this.histories = [];
			this.isAll = false;
			this.page = 1;
		},
		fetchHistories(reset = true) {
			this.isFetching = true;
			if (reset) {
				this.resetHistories();
			}
			const params = this.getFetchParams();
			const callback = (response) => {
				const histories = response.data.data;
				histories.forEach((history) => {
					this.histories.push(history);
				});
				this.page++;
				if (histories.length < this.limit) {
					this.isAll = true;
				}
				this.isFetching = false;
			};
			const errorCallback = () => {
				this.isFetching = false;
				this.isAll = true;
			};
			walletApi.getHistories(params, callback, errorCallback);
		},
		init() {
			this.checkCapabilities();
			this.initFilterDate();
			this.getBalance();
			this.getOnProcessWithdrawal();
			this.fetchHistories();
			this.fetchOverviews();
			this.getStashBalance();
		},
		setFilterDate(filterDate) {
			this.filterDate = filterDate;
			const since = dayjs(filterDate.start).format('YYYY-MM-DD');
			const until = dayjs(filterDate.end).format('YYYY-MM-DD');
			const query = duplicateVar(this.$route.query);
			query.since = since;
			query.until = until;
			const params = { query };
			this.$router.push(params);
			this.fetchHistories(true);
			this.fetchOverviews();
		},
		onKeywordChange() {
			const self = this;
			this.ooDelay(() => {
				self.fetchHistories();
				self.fetchOverviews();
			}, 1000);
		},
		handleScroll() {
			const pageHeight = document.documentElement.offsetHeight;
			const windowHeight = window.innerHeight;
			const scrollPosition = window.scrollY;
			const offset = 50;
			const isOnBottom = pageHeight - offset <= windowHeight + scrollPosition;
			if (isOnBottom) {
				if (!this.isFetching && !this.isAll) {
					this.fetchHistories(false);
				}
			}
		},
		changeSorting(key) {
			const isSame = this.sort_by === key;
			this.sort_by = key;
			const sort = isSame && this.sort === 'asc' ? 'desc' : 'asc';
			this.sort = sort;
			// Re-fetch
			this.fetchHistories(true);
		},
		sortClass(key) {
			const sameKey = this.sort_by === key;
			const className = sameKey ? `th-sort-${this.sort}` : '';
			return className;
		},
		checkCapabilities() {
			if (!this.isOwner && !this.isUserCan('wallet_view')) {
				this.$router.push('/');
			} else {
				this.isRouteChecked = true;
			}
		},
		formatDate(dateString) {
			const format = 'DD-MM-YYYY - HH:mm';
			const timezone = this.user.timezone;
			const formattedDate = this.moment.utc(dateString).tz(timezone).format(format);
			return dateString && dateString !== '' ? formattedDate : '';
		},
		initFilterDate() {
			if (this.since && this.since !== '' && this.until && this.until !== '') {
				const format = 'YYYY-MM-DD';
				const since = dayjs(this.since).format(format);
				const until = dayjs(this.until).format(format);
				const filterDate = {
					start: since,
					end: until,
				};
				this.filterDate = filterDate;
			}
		},
		getMemberName(id) {
			const member = this.members.find(member => member._id === id);
			return member ? member.name : '';
		},
		getFetchParams() {
			const dateFormat = 'YYYY-MM-DD';
			const startDate = dayjs(this.filterDate.start).format(dateFormat);
			const endDate = dayjs(this.filterDate.end).format(dateFormat);
			const params = {
				limit: this.limit,
				sort_by: this.sort_by,
				sort: this.sort,
				page: this.page,
				since: startDate,
				until: endDate,
				type: this.typeSelected,
				status: this.statusSelected,
			};

			if (this.orderId) {
				params.order_id = this.orderId;
			}

			const keyword = this.keyword.trim();
			if (keyword && keyword !== '') {
				params.keyword = keyword;
			}

			return params;
		},
		getBalance() {
			this.isFetchingBalance = true;
			const callback = (response) => {
				this.isFetchingBalance = false;
				const wallet = response.data;
				this.balance = wallet.balance;
			};
			const errorCallback = (e) => {
				this.isFetchingBalance = false;
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Balance',
					text: message,
				});
			};
			walletApi.getBalance(callback, errorCallback);
		},
		getOnProcessWithdrawal() {
			this.isFetchingWithdrawals = true;
			const callback = (response) => {
				const total = response.data;
				this.withdrawn = total;
				this.isFetchingWithdrawals = false;
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Balance',
					text: message,
				});
				this.isFetchingWithdrawals = false;
			};
			walletApi.getOnProcessWithdrawal(callback, errorCallback);
		},
		withdraw() {
			if (this.isWithdrawing) {
				return;
			}
			this.isWithdrawing = true;
			const value = parseInt(this.withdrawValue);
			const description = this.withdrawDescription;

			const metas = [
				{ key: 'bank', value: this.bankAccount.label },
				{ key: 'account_number', value: this.bankAccount.account_number },
				{ key: 'account_name', value: this.bankAccount.account_name },
			];
			const metasString = JSON.stringify(metas);

			const params = {
				value,
				description,
				metas: metasString,
				password: this.password,
			};

			const callback = (response) => {
				this.isWithdrawing = false;
				const message = response.message;
				if (response.error === 0) {
					this.balance -= value;
					this.withdrawn = parseInt(this.withdrawn) + value;

					const history = response.data;
					this.histories.unshift(history);

					this.$notify({
						group: 'app',
						type: 'success',
						title: 'Withdraw Request Success',
						text: message,
					});
					this.withdrawValue = 0;
					// Hide popup
					this.isWithdrawalVisible = false;

					this.resetOtp();
				} else {
					this.$notify({
						group: 'app',
						type: 'error',
						title: 'Withdraw Request Failed',
						text: message,
					});
				}
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.isWithdrawing = false;
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Withdraw Request Failed',
					text: message,
				});
			};
			walletApi.withdraw(params, callback, errorCallback);
		},
		exportRevenue(type = 'cod_revenue') {
			this.isExporting = true;
			const dateFormat = 'YYYY-MM-DD';
			const startDate = dayjs(this.filterDate.start).format(dateFormat);
			const endDate = dayjs(this.filterDate.end).format(dateFormat);
			const params = {
				sort_by: this.sort_by,
				sort: this.sort,
				since: startDate,
				until: endDate,
				type,
			};
			const callback = (response) => {
				const url = response.data;
				if (url && url !== '') {
					downloadFile(url);
				} else {
					const message = 'No transactions with the selected type';
					this.$notify({
						group: 'app',
						type: 'warn',
						title: 'Export Transactions',
						text: message,
					});
				}
				this.isExporting = false;
			};
			const errorCallback = () => {
				this.isExporting = false;
			};
			walletApi.exportHistories(params, callback, errorCallback);
		},
		showWithdraw() {
			this.withdrawValue = Number(this.balance);
			this.isWithdrawalVisible = true;
		},
		formatNumber(number) {
			return formatNumberPlain(number);
		},
		showDetails(history) {
			if (this.history && this.history.id === history.id) {
				this.history = null;
				return;
			}
			this.history = duplicateVar(history);
			if (!history.details) {
				const callback = (response) => {
					const historyItem = response.data;
					this.history = historyItem;
					this.updateHistory(historyItem);
				};
				const errorCallback = () => {};
				walletApi.getHistory(history.id, callback, errorCallback);
			}
		},
		updateHistory(history) {
			const index = this.histories.findIndex(({ id }) => id === history.id);
			if (index !== -1) {
				this.$set(this.histories, index, history);
			}
		},
		setWithdrawValue(value) {
			this.withdrawValue = Number(value);
		},
		exportHistories() {
			const creditHistories = this.histories.filter(({ mutation }) => mutation === 'credit');
			const codFeePercentage = 3;
			let totalGoodsValue = 0;
			let totalShippingCost = 0;
			let totalCodFee = 0;
			let totalAmount = 0;
			let totalFinalAmount = 0;
			const mapper = (historyItem, index) => {
				const number = index + 1;
				const details = historyItem.details;
				const detailTotalPrice = details.find(({ type }) => type === 'total_price');
				const detailShippingCost = details.find(({ type }) => type === 'shipping_cost');
				const detailCodFee = details.find(({ type }) => type === 'cod_fee');
				const amountReceived = detailTotalPrice ? detailTotalPrice.value : 0;
				const shippingCost = detailShippingCost ? detailShippingCost.value : 0;
				const codFee = detailCodFee ? detailCodFee.value : 0;
				const goodsValue = amountReceived - shippingCost;
				const finalAmount = historyItem.value;
				const createdAt = dayjs(historyItem.created_at).format('DD-MM-YYYY HH:mm');

				const historyData = [];
				// No.
				historyData.push(number);
				// Trx ID
				historyData.push(historyItem.id);
				// Created Date
				historyData.push(createdAt);
				// Description
				historyData.push(historyItem.description);
				// Product Price / Goods Value
				historyData.push(goodsValue);
				// Shipping Cost
				historyData.push(shippingCost);
				// Amount Received
				historyData.push(amountReceived);
				// COD Fee Percentage
				historyData.push(`${codFeePercentage}% from Goods Value`);
				// COD Fee
				historyData.push(codFee);
				// Final Amount
				historyData.push(finalAmount);

				totalGoodsValue += goodsValue;
				totalShippingCost += shippingCost;
				totalCodFee += codFee;
				totalAmount += amountReceived;
				totalFinalAmount += finalAmount;

				return historyData;
			};
			const histories = creditHistories.map(mapper);

			// Header
			const header = ['No.', 'Transaction ID', 'Created Date', 'Description', 'Goods Value', 'Shipping Charge', 'Amount Received', 'COD Fee (%)', 'COD Fee (Rp)', 'Total Amount - COD Fee - Shipping Charge'];
			histories.unshift(header);

			const summaryRow = [
				'', // No
				'', // Trx ID
				'', // Created Date
				'', // Description
				totalGoodsValue, // Goods Value
				totalShippingCost, // Shipping Charge
				totalAmount, // Amount Received
				'', // COD Fee %
				totalCodFee, // COD Fee (Rp)
				totalFinalAmount, // Final Amount
			];

			histories.push(summaryRow);

			// Create Worksheet
			const ws = XLSX.utils.aoa_to_sheet(histories, header);

			// Create Workbook
			const wb = XLSX.utils.book_new();

			/* Add the worksheet to the workbook */
			XLSX.utils.book_append_sheet(wb, ws, 'Credits Histories');

			const dateFormat = 'YYYY-MM-DD';
			const startDate = dayjs(this.filterDate.start).format(dateFormat);
			const endDate = dayjs(this.filterDate.end).format(dateFormat);

			const fileName = `credits-${startDate}-to-${endDate}.xlsx`;

			// Export Excel File
			XLSX.writeFile(wb, fileName);
		},
		fetchOverviews() {
			const dateFormat = 'YYYY-MM-DD';
			const startDate = dayjs(this.filterDate.start).format(dateFormat);
			const endDate = dayjs(this.filterDate.end).format(dateFormat);
			const params = {
				since: startDate,
				until: endDate,
			};
			const callback = (response) => {
				const overviews = response.data;
				this.overviews = overviews;
			};
			const errorCallback = () => {
			};
			walletApi.getOverviews(params, callback, errorCallback);
		},
		getStashBalance() {
			this.isFetchingStash = true;
			const callback = (response) => {
				this.isFetchingStash = false;
				const total = response.data;
				this.stashTotal = total;
			};
			const errorCallback = (e) => {
				this.isFetchingStash = false;
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Stash Balance',
					text: message,
				});
			};
			walletStashApi.getTotal(callback, errorCallback);
		},
		generateOtp() {
			this.isGeneratingOtp = true;
			this.isOtpVisible = true;

			const payloadJson = JSON.stringify(this.otpPayload);
			const params = {
				type: 'withdrawal',
				payload: payloadJson,
			};
			const callback = () => {
				this.isGeneratingOtp = false;
				// Start OTP Timer Interval
				this.startOtpTimer();
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				console.log(message);
				// this.$notify({
				// 	group: 'app',
				// 	type: 'error',
				// 	title: 'OTP Code',
				// 	text: message,
				// });
				this.isGeneratingOtp = false;
				this.startOtpTimer();
			};
			otpApi.generate(params, callback, errorCallback);
		},
		startOtpTimer() {
			this.otpTimerValue = parseInt(this.maxOtpTime);
			const otpInterval = setInterval(() => {
				this.otpTimerValue--;
				if (this.otpTimerValue === 0) {
					clearInterval(this.otpTimerInterval);
				}
			}, 1000);

			this.otpTimerInterval = otpInterval;
		},
		validateOtp() {
			this.isValidatingOtp = true;
			const code = this.otpCode.trim();
			const params = {
				code,
			};
			const callback = (response) => {
				console.log(response);
				this.isValidatingOtp = false;
				this.withdraw();
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'OTP Validation',
					text: message,
				});
				this.isValidatingOtp = false;
			};
			otpApi.validate(params, callback, errorCallback);
		},
		cancelWithdraw() {
			this.isWithdrawalVisible = false;
			this.resetOtp();
		},
		resetOtp() {
			if (this.otpTimerInterval) {
				clearInterval(this.otpTimerInterval);
				this.otpTimerInterval = null;
			}
			this.otpTimerValue = parseInt(this.maxOtpTime);
			// this.isOtpValid = false;
			this.isOtpVisible = false;
			this.isGeneratingOtp = false;
			this.otpCode = '';
		},
		getTypeLabel(type) {
			let label = type;
			if (type === 'cod_revenue') {
				label = 'COD Revenue';
			} else {
				label = toTitleCase(type.replace(/_/g, ' '));
			}
			return label;
		},
		checkPassword() {
			this.isCheckingPassword = true;

			const password = this.password.trim();

			const params = {
				password,
			};
			const callback = () => {
				this.isCheckingPassword = false;
				this.generateOtp();
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Password',
					text: message,
				});
				this.isCheckingPassword = false;
			};
			user.validatePassword(params, callback, errorCallback);
		},
		onOrderIdChanged() {
			this.ooDelay(() => {
				this.fetchHistories(true);
			}, 1000);
		},
		getHandlingFee() {
			return this.formatNumber(this.overviews?.handling_fee);
		},
	},
	computed: {
		...mapGetters({
			user: 'user',
			isAdmin: 'isAdmin',
			isOwner: 'isOwner',
			isUserCan: 'isUserCan',
			isHasFeature: 'isHasFeature',
			isTester: 'isTester',
			bankAccounts: 'bankAccounts',
			presetRangeLabel: 'presetRangeLabel',
			filterDatePresets: 'filterDatePresets',
			members: 'members/all',
		}),
		since() {
			const since = this.$route.query.since;
			return since;
		},
		until() {
			const until = this.$route.query.until;
			return until;
		},
		balanceDisplay() {
			return formatNumberPlain(this.balance);
		},
		withdrawnDisplay() {
			return formatNumberPlain(this.withdrawn);
		},
		isWithdrawValid() {
			return this.isWithdrawValueValid && !!this.bankAccount && this.password !== '';
		},
		isWithdrawValueValid() {
			return this.withdrawValue
				&& this.withdrawValue > 0
				&& this.withdrawValue >= this.withdrawTreshold
				&& this.withdrawValue <= this.balance;
		},
		bankAccountsOptions() {
			const bankAccounts = this.bankAccounts.map((bankAccount) => {
				bankAccount.id = `${bankAccount.name}-${bankAccount.account_number}`;
				bankAccount.text = `${bankAccount.label} - ${bankAccount.account_name} - ${bankAccount.account_number}`;
				return bankAccount;
			});
			return bankAccounts;
		},
		withdrawDescription() {
			const value = formatNumberPlain(this.withdrawValue);
			const description = `Withdraw ${value} to ${this.bankAccount.text}`;
			return description;
		},
		isCanWithdraw() {
			return this.isOwner || this.isUserCan('wallet_withdraw');
			// return false;
		},
		stashDisplay() {
			return formatNumberPlain(this.stashTotal);
		},
		sensoredEmail() {
			const email = this?.user?.email ?? '';
			const sensoredEmail = this.isOwner ? sensorEmailAddress(email) : 'Owner account ini';
			return sensoredEmail;
		},
		otpTimer() {
			const timerLabel = generateTimerLabel(this.otpTimerValue);
			return timerLabel;
		},
		otpPayload() {
			const destination = this.bankAccount ? this.bankAccount.text : '';
			const payload = {
				value: this.withdrawValue,
				destination,
			};
			return payload;
		},
		isOtpValid() {
			return this.otpCode && this.otpCode.length === 6;
		},
		isWdDisabled() {
			return this.isHasFeature('withdraw_disabled');
		},
		isCommissionUserAllowed() {
			return this.user.email === 'inventtro.keu@gmail.com';
		},
	},
	watch: {
		user() {
			this.init();
		},
		typeSelected() {
			if (this.typeSelected) {
				this.fetchHistories(true);
			}
		},
		statusSelected() {
			if (this.statusSelected) {
				this.fetchHistories(true);
			}
		},
	},
	created() {
		if (this.user) {
			this.init();
		}
		window.addEventListener('scroll', this.handleScroll);
		document.addEventListener('scroll', this.onDocumentScroll, true);
	},
	destroyed() {
		window.removeEventListener('scroll', this.handleScroll);
		document.removeEventListener('scroll', this.onDocumentScroll, true);
	},
};
